/* PRD-037 — Componentes de gestion compartidos (V4). */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PartnerCommerce } from '@services/partnerService';
import { TextInputV4 } from '@components/v4/Input/Input';
import { useDesktop } from '../utils/hooks/useDesktop';
import { Badge, C } from './ui';
import { fmtDate } from './useCommerces';

export const Stat = ({ label, value, tone }: { label: string; value: ReactNode; tone?: string }) => (
  <div
    style={{
      flex: 1,
      minWidth: 120,
      background: C.white,
      border: `1px solid ${C.cardBorder}`,
      borderRadius: 12,
      padding: '16px 18px',
    }}
  >
    <div style={{ fontSize: 26, fontWeight: 700, color: tone || C.primary, lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{label}</div>
  </div>
);

// Acciones rapidas por fila (solo drafts). El claim-link se genera y se muestra inline sin entrar
// al detalle. Cada callback es opcional: si no se pasa `quick`, la fila es solo navegable al detalle.
export interface CommerceQuickActions {
  onView: () => void;
  onEditContact: () => void;
  onConfigure: () => void;
  onLoadMenu: () => void;
  onClaimLink: () => Promise<string>;
}

const RowBtn = ({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      fontFamily: 'Poppins, sans-serif',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.02em',
      color: C.secondary,
      background: C.white,
      border: `1px solid ${C.secondary}`,
      borderRadius: 6,
      padding: '6px 11px',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </button>
);

// Línea de metadato break-safe: el valor (email/teléfono) nunca desborda la card.
const MetaLine = ({ children }: { children: ReactNode }) => (
  <div
    style={{
      fontSize: 13,
      color: C.muted,
      lineHeight: 1.45,
      minWidth: 0,
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    }}
  >
    {children}
  </div>
);

export const CommerceItem = ({
  c,
  quick,
  first,
}: {
  c: PartnerCommerce;
  quick?: CommerceQuickActions;
  first?: boolean;
}) => {
  const navigate = useNavigate();
  const isDesktop = useDesktop();
  const claimed = c.estado === 'reclamado';
  const pending = claimed && c.plan_pending;
  const [link, setLink] = useState<string | null>(null);
  const [genLoading, setGenLoading] = useState(false);
  const [linkError, setLinkError] = useState(false);

  const onClaimLink = async () => {
    if (!quick) return;
    setLinkError(false);
    setGenLoading(true);
    try {
      setLink(await quick.onClaimLink());
    } catch {
      setLinkError(true);
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <div
      style={{
        borderTop: first ? 'none' : `1px solid ${C.cardBorder}`,
        padding: first ? '0 0 16px' : '16px 0',
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={() => c.id_comercio && navigate(`/partner/comercios/${c.id_comercio}`)}
          style={{
            flex: '1 1 240px',
            textAlign: 'left',
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0 }}>
            <span
              style={{
                fontWeight: 600,
                fontSize: 15,
                color: C.primary,
                lineHeight: 1.3,
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                minWidth: 0,
              }}
            >
              {c.negocio_nombre || 'Comercio sin nombre'}
            </span>
            <Badge tone={pending ? 'pending' : claimed ? 'claimed' : 'draft'}>
              {pending ? 'Pago pendiente' : claimed ? 'Activado' : 'Por activar'}
            </Badge>
          </div>
          <MetaLine>
            <span style={{ fontWeight: 500, color: C.primary }}>{c.owner_nombre || 'Sin dueño'}</span>
            {c.owner_telefono ? <span style={{ color: C.muted }}>{` · ${c.owner_telefono}`}</span> : null}
          </MetaLine>
          {c.owner_correo ? <MetaLine>{c.owner_correo}</MetaLine> : null}
          <MetaLine>
            <span style={{ fontWeight: 500 }}>{c.tipo_plan || 'FREEMIUM'}</span>
            {c.created_at ? ` · creado ${fmtDate(c.created_at)}` : ''}
          </MetaLine>
          {pending && (
            <MetaLine>
              <span style={{ color: C.warning, fontWeight: 500 }}>
                El pago no se completó{c.owner_usuario ? ` · usuario: ${c.owner_usuario}` : ''}
              </span>
            </MetaLine>
          )}
        </button>

        {quick ? (
          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              width: isDesktop ? 'auto' : '100%',
              flexShrink: 0,
              justifyContent: isDesktop ? 'flex-end' : 'flex-start',
            }}
          >
            <RowBtn onClick={quick.onView}>Ver</RowBtn>
            {!claimed && (
              <>
                <RowBtn onClick={quick.onConfigure}>Preparar</RowBtn>
                <RowBtn onClick={quick.onLoadMenu}>Cargar menú</RowBtn>
                <RowBtn onClick={quick.onEditContact}>Editar contacto</RowBtn>
                <RowBtn onClick={onClaimLink} disabled={genLoading}>
                  {genLoading ? 'Generando...' : 'Invitar al dueño'}
                </RowBtn>
              </>
            )}
          </div>
        ) : null}
      </div>

      {linkError && (
        <div style={{ fontSize: 12, color: C.danger, marginTop: 8 }}>No se pudo generar el enlace de activación.</div>
      )}
      {link && (
        <div style={{ marginTop: 10 }}>
          <TextInputV4
            value={link}
            onChange={() => {}}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Enlace de activación"
          />
        </div>
      )}
    </div>
  );
};
